import React from 'react';
import { Link } from 'react-router-dom';
import {
  IndianRupee,
  Upload,
  Layers,
  LayoutDashboard,
  MessageSquare,
  BarChart3,
  ArrowRight
} from 'lucide-react';
import styles from './Products.module.css';

/* The lead product, then the four products it is delivered and extended
   through. Content follows the product flyers. Each `path` is the child
   page for that product. */
const leadProduct = {
  name: 'One Rupee ERP',
  tagline: 'The analytics layer ERPs never built',
  description:
    'A Power BI-native platform: onboard data your way, model it properly, ask questions in plain English, and benchmark against peers.',
  path: '/products/one-rupee-erp',
  features: [
    { Icon: Upload, title: 'Onboard data your way', text: 'A ready-made template, or design your own.' },
    { Icon: Layers, title: 'Real semantic modeling', text: 'Measures built to match how you track performance.' },
    { Icon: LayoutDashboard, title: 'Reports built for you', text: 'Native on day one, or custom to specification.' },
    { Icon: MessageSquare, title: 'Ask it in plain English', text: 'A data agent answers from the semantic model.' },
    { Icon: BarChart3, title: 'Benchmarks no ERP offers', text: 'Across schools by size, region and affiliation.' }
  ]
};

const productCards = [
  {
    step: '1',
    icon: '🖥️',
    name: 'Desktop Analytics',
    badge: 'Standardized',
    path: '/products/desktop-analytics',
    description:
      'Maximised convenience and speed, ideal for rapid insights using standardised reporting. Delivers web-based views of pre-designed templates.'
  },
  {
    step: '2',
    icon: '☁️',
    name: 'Enterprise Managed Analytics',
    badge: 'AnalyticaX Hosted',
    path: '/products/enterprise-managed-analytics',
    description:
      'Custom embedded analytics without internal infrastructure overhead. Delivers app registration for Power BI embed with a client-specific URL.'
  },
  {
    step: '3',
    icon: '🔒',
    name: 'Enterprise Native Analytics',
    badge: 'Client Hosted',
    path: '/products/enterprise-native-analytics',
    description:
      'Maximised data privacy and customisation for strict data governance requiring zero external data movement. Native UI built within client architecture.'
  },
  {
    step: '4',
    icon: '📊',
    name: 'Benchmarks',
    badge: 'Peer Comparison',
    path: '/products/benchmarks',
    description:
      'See where you stand against comparable institutions by size, region and affiliation. Common definitions across enrolment, fee realisation, staffing and outcomes, so the comparison holds.'
  }
];

const Products = () => (
  <section id="products" className={`${styles.productsSection} section`}>
    <div className="container">
      <h2 className="section-title fade-in">Our Products</h2>
      <p className={styles.sectionLead}>
        One platform, delivered through the infrastructure pathway that suits your
        speed, capacity and data privacy requirements — and benchmarked against
        the peers you are measured by.
      </p>

      <div className={styles.productsGrid}>
        <article className={`${styles.productLead} fade-in`}>
          <div className={styles.leadIntro}>
            <span className={styles.leadIcon} aria-hidden="true">
              <IndianRupee size={26} strokeWidth={2.2} />
            </span>
            <h3 className={styles.leadName}>{leadProduct.name}</h3>
            <p className={styles.leadTagline}>{leadProduct.tagline}</p>
            <p className={styles.leadDescription}>{leadProduct.description}</p>
            <Link
              to={leadProduct.path}
              className={styles.leadKnowMore}
              aria-label={`Know more about ${leadProduct.name}`}
            >
              Know More
              <ArrowRight size={15} strokeWidth={2.2} aria-hidden="true" />
            </Link>
          </div>

          <ul className={styles.leadFeatures}>
            {leadProduct.features.map(({ Icon, title, text }) => (
              <li key={title}>
                <span className={styles.featureIcon} aria-hidden="true">
                  <Icon size={16} strokeWidth={2} />
                </span>
                <span className={styles.featureBody}>
                  <span className={styles.featureTitle}>{title}</span>
                  <span className={styles.featureText}>{text}</span>
                </span>
              </li>
            ))}
          </ul>
        </article>

        {productCards.map((product) => (
          <article key={product.name} className={`${styles.productCard} fade-in`}>
            <div className={styles.cardTop}>
              <span className={styles.cardIcon} aria-hidden="true">{product.icon}</span>
              <span className={styles.cardStep} aria-hidden="true">{product.step}</span>
            </div>
            <h3 className={styles.cardName}>{product.name}</h3>
            <p className={styles.cardBadge}>{product.badge}</p>
            <p className={styles.cardDescription}>{product.description}</p>
            <Link
              to={product.path}
              className={styles.cardKnowMore}
              aria-label={`Know more about ${product.name}`}
            >
              Know More
              <ArrowRight size={15} strokeWidth={2.2} aria-hidden="true" />
            </Link>
          </article>
        ))}
      </div>
    </div>
  </section>
);

export default Products;
